import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { getLabelFromCopyOrFromTranslation } from '@tcp/core/src/utils/labels/labelUtilty';
import {
  GlobalGenericLabels,
  GLOBAL_GENERIC_LABELS,
  lblColors,
} from '@tcp/core/src/utils/labels/globalGeneric.labels';
import {
  InteractiveColorChipButton,
  InteractiveColorChipText,
  ColorsCountWrapper,
  ColorsCountText,
  InteractiveColorSwatchBandWrapper,
  ColorSwatchContainer,
} from './styles/ColorSwatches.style';
import { INTERACTIVE_PLP_SLP_COLOR_SWATCH_DIMENSIONS } from '../plpTileConstants';
import getColorSwatchList from './plpTileHelpers/colorSwatchHelper';
import BodyCopy from '../../../../common/atoms/BodyCopy';

const renderTextOrColorSwatches = ({
  colorSwatchList,
  productColorListRef,
  numberOfSwatchesToDisplay,
  clickHandler,
  handleMouseEnter,
  handleMouseLeave,
}) => {
  const maxSwatchLength = numberOfSwatchesToDisplay > 0 ? numberOfSwatchesToDisplay : 6;
  const list = colorSwatchList.filter((val, idx) => idx < maxSwatchLength);
  const colorsLabel = getLabelFromCopyOrFromTranslation(
    `${GLOBAL_GENERIC_LABELS}.${lblColors}`,
    GlobalGenericLabels
  );
  return (
    <InteractiveColorSwatchBandWrapper>
      <ColorsCountWrapper>
        <ColorsCountText fontFamily="secondary">{`${colorSwatchList.length} ${colorsLabel}`}</ColorsCountText>
        <svg
          className="interactive-color-swatches-arrow-toggle"
          xmlns="http://www.w3.org/2000/svg"
          width="8"
          height="5"
          viewBox="0 0 8 5"
          fill="none"
        >
          <path
            d="M0.234394 0.334679C0.547186 0.0219048 1.05277 0.0219048 1.36557 0.334679L3.9999 2.96886L6.63423 0.334678C6.94703 0.0219042 7.45261 0.0219042 7.76541 0.334678C8.0782 0.647452 8.0782 1.15301 7.76541 1.46578L4.56549 4.66552C4.25269 4.97829 3.74711 4.97829 3.43431 4.66552L0.234394 1.46578C0.0783979 1.3098 8.78526e-08 1.10501 6.99499e-08 0.900232C5.20472e-08 0.695449 0.0783978 0.490666 0.234394 0.334679Z"
            fill="white"
          />
        </svg>
      </ColorsCountWrapper>
      <div className="hidden-color-list-element" ref={productColorListRef} />
      <ColorSwatchContainer className="interactive-color-swatch-container">
        <ol className="interactive-color-swatch-list">
          {list?.map((colorSwatch) => {
            return (
              <InteractiveColorChipButton
                key={colorSwatch.id}
                type="button"
                title={colorSwatch.name}
                isActive={colorSwatch.isActive}
                name={colorSwatch.name}
                imageUrl={colorSwatch.imageUrl}
                onClick={() => clickHandler(colorSwatch.id)}
                onMouseEnter={() => handleMouseEnter(colorSwatch.id)}
                onMouseLeave={handleMouseLeave}
              />
            );
          })}
          {productColorListRef?.current?.clientWidth > 0 &&
            maxSwatchLength > 0 &&
            colorSwatchList.length > maxSwatchLength && (
              <InteractiveColorChipText onClick={() => clickHandler(colorSwatchList[0]?.id)}>
                <BodyCopy
                  className="interactive-color-text"
                  fontFamily="secondary"
                  fontSize="fs12"
                  component="div"
                >
                  {`+${colorSwatchList?.length - maxSwatchLength}`}
                </BodyCopy>
              </InteractiveColorChipText>
            )}
        </ol>
      </ColorSwatchContainer>
    </InteractiveColorSwatchBandWrapper>
  );
};

const ColorSwatches = ({
  onQuickViewOpenClick,
  saveDataForQv,
  productPartNo,
  TCPSwatches,
  assetHost,
  assetPath,
  swatchImg,
  relatedSwatchImages,
  tcpColor,
  auxDescription,
  isBundleProduct,
  handleViewBundleClick,
  isNewSwatchUrlEnabled,
  isSLPTile,
  handleMouseEnter,
  handleMouseLeave,
}) => {
  const [numberOfSwatchesToDisplay, setNumberOfSwatchesToDisplay] = useState(0);
  const productColorListRef = useRef(null);

  const { colorSwatchList } = getColorSwatchList({
    productPartNo,
    TCPSwatches,
    assetHost,
    assetPath,
    swatchImg,
    relatedSwatchImages,
    tcpColor,
    auxDescription,
    isNewSwatchUrlEnabled,
  });

  useEffect(() => {
    const { width, remainingSwatchesTextWidth, containerGap } =
      INTERACTIVE_PLP_SLP_COLOR_SWATCH_DIMENSIONS;
    const colorListWidth = productColorListRef?.current?.clientWidth;

    const colorSwatchWidth = width + containerGap;

    if (colorSwatchList.length * colorSwatchWidth - containerGap > colorListWidth) {
      const maxSwatches = Math.floor(
        (colorListWidth - remainingSwatchesTextWidth) / colorSwatchWidth
      );

      setNumberOfSwatchesToDisplay(maxSwatches);
    } else {
      setNumberOfSwatchesToDisplay(colorSwatchList.length);
    }
  }, [colorSwatchList]);

  const clickHandler = (swatchId) => {
    requestAnimationFrame(() => {
      if (isBundleProduct) {
        handleViewBundleClick();
        return;
      }
      saveDataForQv(swatchId);
      onQuickViewOpenClick({
        colorProductId: swatchId,
        fromPage: isSLPTile ? 'SLP' : 'PLP',
      });
    });
  };

  return colorSwatchList?.length > 1
    ? renderTextOrColorSwatches({
        colorSwatchList,
        productColorListRef,
        numberOfSwatchesToDisplay,
        clickHandler,
        handleMouseEnter,
        handleMouseLeave,
      })
    : null;
};

ColorSwatches.propTypes = {
  onQuickViewOpenClick: PropTypes.func,
  saveDataForQv: PropTypes.func,
  handleMouseEnter: PropTypes.func,
  handleMouseLeave: PropTypes.func,
  productPartNo: PropTypes.string,
  TCPSwatches: PropTypes.string,
  assetHost: PropTypes.string.isRequired,
  assetPath: PropTypes.string.isRequired,
  swatchImg: PropTypes.string,
  tcpColor: PropTypes.string,
  auxDescription: PropTypes.string,
  relatedSwatchImages: PropTypes.array,
  isNewSwatchUrlEnabled: PropTypes.bool,
  isSLPTile: PropTypes.bool,
};

ColorSwatches.defaultProps = {
  onQuickViewOpenClick: () => {},
  saveDataForQv: () => {},
  handleMouseEnter: () => {},
  handleMouseLeave: () => {},
  productPartNo: '',
  TCPSwatches: '',
  swatchImg: '',
  tcpColor: '',
  auxDescription: '',
  relatedSwatchImages: [],
  isNewSwatchUrlEnabled: false,
  isSLPTile: false,
};

export default ColorSwatches;
